<template>
  <section class="pb-2">
    <b-row class="mb-1">
      <b-col sm="4">
        <span class="mr-50">{{ $tk('ClientPortal.MasterTable.ShowDataPer') }}</span>
        <b-dropdown
          ref="dateRangePickerDropdown"
          menu-class="py-0"
          variant="outline-dark"
          :disabled="loading"
          class="bg-white ml-0 mb-50 mb-md-0"
          no-caret
          size="sm"
        >
          <template #button-content>
            {{ defaultRange }}
            <GjIcon
              name="Calendar"
              class="ml-50"
            />
          </template>
          <div class="p-2">
            <date-range-picker
              ref="dateRangePicker"
              :ranges="predefinedRanges"
              default-range-select="defaultRange"
              :allow-compare="false"
              @submit="submitDateRange"
              @cancel="closeDateRangePicker"
            />
          </div>
        </b-dropdown>
      </b-col>
      <b-col
        sm="8"
        class="text-right"
      >
        <b-dropdown
          id="filterDropdown"
          ref="filterDropdown"
          class="bg-white"
          variant="outline-dark"
          size="sm"
          :disabled="loading"
          right
          no-caret
          menu-class="p-50"
        >
          <template #button-content>
            <GjIcon
              name="Filters"
              class="mr-50"
            />
            {{ $tk('ClientPortal.MasterTable.Filters') }}
          </template>
          <!--Saved filters -->
          <b-dropdown
            v-if="!loading"
            id="dropdown-multilevel"
            menu-class="p-50 filter-dropdown"
            no-caret
            dropleft
            block
            variant="white border-0 justify-content-between"
          >
            <template #button-content>
              {{ $tk('ClientPortal.Filters.SavedFilters') }}
              <GjIcon name="ArrowRight" />
            </template>
            <template v-if="wrapper.savedPropertyFilters.length > 0">
              <template v-for="(savedPropertyFilter, savedPropertyFilterIndex) in wrapper.savedPropertyFilters">
                <b-dropdown-item
                  :key="'saved-filter-'+savedPropertyFilterIndex"
                  @click="setSavedPropertyFilters(savedPropertyFilter)"
                >
                  {{ savedPropertyFilter.name }}
                  <GjIcon
                    v-if="selectedPropertyFiltersWrapper.id == savedPropertyFilter.id"
                    :key="'saved-filter-checked-'+savedPropertyFilterIndex"
                    name="Check"
                  />
                </b-dropdown-item>
              </template>
            </template>
            <template v-else>
              <b-dropdown-item
                disabled
              >
                {{ $tk('ClientPortal.Filters.ThereAreNoSavedFilters') }}
              </b-dropdown-item>
            </template>
          </b-dropdown>
          <template v-for="(propertyFilter, propertyFilterIndex) in wrapper.searchFilters">
            <b-dropdown
              id="dropdown-multilevel"
              :key="`search-filter-${propertyFilterIndex}`"
              menu-class="p-50"
              dropleft
              no-caret
              block
              variant="white border-0 justify-content-between"
            >
              <template #button-content>
                {{ propertyFilter.name }}
                <GjIcon name="ArrowRight" />
              </template>
              <template v-if="propertyFilter.type == 1">
                <template v-for="(childPropertyFilter, childPropertyFilterIndex) in propertyFilter.children">
                  <b-dropdown-item
                    :key="childPropertyFilterIndex"
                    class="multilevel-dropdown"
                    :class="isPropertyFilterAdded(childPropertyFilter) ? 'selected-filter' : ''"
                    @click="addPropertyFilter(childPropertyFilter)"
                  >
                    {{ childPropertyFilter.name }}
                    <GjIcon
                      v-if="isPropertyFilterAdded(childPropertyFilter)"
                      name="Check"
                    />
                  </b-dropdown-item>
                </template>
              </template>
              <template v-if="propertyFilter.type == 4">
                <template v-for="(availableDisplayValue, availableValue) in propertyFilter.availableValues">
                  <b-dropdown-item
                    :key="availableValue"
                    :class="isPropertyFilterValueAdded(propertyFilter, availableValue) ? 'selected-filter' : ''"
                    @click="addPropertyFilterValue(propertyFilter, availableValue)"
                  >{{ availableDisplayValue }}
                    <GjIcon
                      v-if="isPropertyFilterValueAdded(propertyFilter, availableValue)"
                      name="Check"
                    />
                  </b-dropdown-item>
                </template>
              </template>
            </b-dropdown>
          </template>
          <template v-for="(propertyFilter, propertyFilterIndex) in wrapper.propertyFilters">
            <b-dropdown
              id="dropdown-multilevel"
              :key="`property-filters-${propertyFilterIndex}`"
              menu-class="p-50 filter-dropdown"
              dropleft
              no-caret
              block
              variant="white border-0 justify-content-between"
            >
              <template #button-content>
                {{ propertyFilter.name }}
                <GjIcon name="ArrowRight" />
              </template>
              <template v-if="propertyFilter.type == 1">
                <template v-for="(childPropertyFilter, childPropertyFilterIndex) in propertyFilter.children">
                  <b-dropdown-item
                    :key="childPropertyFilterIndex"
                    @click="addPropertyFilter(childPropertyFilter)"
                  >
                    {{ childPropertyFilter.name }}
                    <GjIcon
                      v-if="isPropertyFilterAdded(childPropertyFilter)"
                      name="Check"
                    />
                  </b-dropdown-item>
                </template>
              </template>
              <template v-if="propertyFilter.type == 4">
                <template v-for="(availableDisplayValue, availableValue) in propertyFilter.availableValues">
                  <b-dropdown-item
                    :key="availableValue"
                    @click="addPropertyFilterValue(propertyFilter, availableValue)"
                  >{{ availableDisplayValue }}
                    <GjIcon
                      v-if="isPropertyFilterValueAdded(propertyFilter, availableValue)"
                      name="Check"
                    />
                  </b-dropdown-item>
                </template>
              </template>
            </b-dropdown>
          </template>
          <b-dropdown-divider />
          <b-dropdown-item
            id="clear__section"
            @click="clearPropertyFilters"
          >
            <GjIcon
              name="Close"
              class="mr-50"
            />
            {{ $tk('ClientPortal.Filters.ClearFilters') }}
          </b-dropdown-item>
        </b-dropdown>
      </b-col>

    </b-row>
    <!--Filters-->
    <b-card
      v-if="selectedPropertyFilters.length > 0"
      body-class="d-flex flex-wrap mb-n50 py-1"
      footer-class="py-1"
    >
      <template v-for="(propertyFilter, propertyFilterIndex) in selectedPropertyFilters">
        <div
          v-if="propertyFilterValues[propertyFilter.property]"
          :key="propertyFilterIndex"
          class="filter-tag p-50 mr-50 mb-50 d-flex align-items-center"
          :class="propertyFilter.inEditMode?'border-primary bg-primary-light':''"
        >
          <span
            :key="propertyFilterIndex"
            class="text-nowrap mr-50"
          >{{ propertyFilter.name }}:</span>
          <div
            v-if="propertyFilter.type == 2 || propertyFilter.type == 3"
            class="d-inline-block d-flex align-items-center"
          >
            <template v-if="propertyFilter.inEditMode">
              <b-form-select
                :key="propertyFilter.property + '-' + propertyFilterIndex"
                v-model="propertyFilterValues[propertyFilter.property].operator"
                class="mr-50"
                size="sm"
              >
                <b-form-select-option
                  v-for="(operatorDisplayValue, operator) in propertyFilter.operators"
                  :key="propertyFilter.property + '-' + operator"
                  :value="operator"
                >
                  {{ operatorDisplayValue }}
                </b-form-select-option>
              </b-form-select>
              <b-form-input
                v-if="propertyFilter.type == 2"
                :key="'input-'+propertyFilterIndex"
                ref="propertyFilterInput"
                v-model.number="propertyFilterValues[propertyFilter.property].value"
                type="number"
                min="0"
                @keyup.enter="closeFilterEdit(propertyFilter)"
              />
              <div
                v-else
              >
                <b-form-tags
                  :key="'tags-'+propertyFilterIndex"
                  ref="propertyFilterFormTags"
                  v-model="propertyFilterValues[propertyFilter.property].value"
                  size="sm"
                  tag-class="sm"
                  class="d-inline-block"
                  input-id="tags-pills"
                  remove-on-delete
                  add-button-variant="w-25 h-25 p-0"
                  autofocus
                  tag-variant="outline-primary"
                  :placeholder="$tk('ClientPortal.Form.AddTagPlaceholder')"
                  @keydown.tab.native="addPropertyFilterTagValue($event,propertyFilterValues[propertyFilter.property])"
                >
                  <!-- <template #add-button-text>
                    <div>
                      <GjIcon name="Plus" />
                      add-button-variant="outline-primary w-25 h-25 p-0"
                    </div>
                  </template> -->
                </b-form-tags>
              </div>
              <b-button
                :key="'check-'+propertyFilterIndex"
                size="sm"
                class="pr-0"
                variant="link"
                @click="closeFilterEdit(propertyFilter)"
              >
                <GjIcon name="Checkmark" />
              </b-button>
            </template>
            <template v-else>
              <div class="d-inline-block">
                <div
                  v-if="propertyFilter.type == 3"
                  class=" d-flex align-items-center"
                >
                  <b-form-select
                    v-model="propertyFilter.operators[propertyFilterValues[propertyFilter.property].operator]"
                    class="mr-50"
                    disabled
                    size="sm"
                  >
                    <b-form-select-option
                      v-for="(operatorDisplayValue, operator) in propertyFilter.operators"
                      :key="operator"
                      :value="operatorDisplayValue"
                    >
                      {{ operatorDisplayValue }}
                    </b-form-select-option>
                  </b-form-select>
                  <span
                    v-for="item in propertyFilterValues[propertyFilter.property].value"
                    :key="item.id"
                    :class="propertyFilterValues[propertyFilter.property].value.length > 1 ? 'ml-25' : ''"
                  >
                    {{ item }}
                  </span>
                </div>
                <div
                  v-else
                  class=" d-flex align-items-center"
                >
                  <b-form-select
                    v-model="propertyFilter.operators[propertyFilterValues[propertyFilter.property].operator]"
                    class="mr-50"
                    disabled
                    size="sm"
                  >
                    <b-form-select-option
                      v-for="(operatorDisplayValue, operator) in propertyFilter.operators"
                      :key="operator"
                      :value="operatorDisplayValue"
                    >
                      {{ operatorDisplayValue }}
                    </b-form-select-option>
                  </b-form-select>
                  <b-form-input
                    v-model.number="propertyFilterValues[propertyFilter.property].value"
                    type="number"
                    min="0"
                    disabled
                  />
                </div>
              </div>
              <b-button
                size="sm"
                class="pr-0"
                variant="link"
                @click="openFilterEdit(propertyFilter)"
              >
                <GjIcon
                  class="text-secondary"
                  name="Edit"
                />
              </b-button>
            </template>
          </div>
          <div
            v-else-if="propertyFilter.type == 4"
            class="d-inline-block d-flex align-items-center"
          >
            <template
              v-if="propertyFilter.inEditMode"
              @keyup.enter="saveQuickEditModal(editModal, entity)"
            >
              <b-form-select
                :key="'select-'+propertyFilterIndex"
                v-model="propertyFilterValues[propertyFilter.property].operator"
                class="mr-50"
                size="sm"
              >
                <b-form-select-option
                  v-for="(operatorDisplayValue, operator) in propertyFilter.operators"
                  :key="operator"
                  :value="operator"
                >
                  {{ operatorDisplayValue }}
                </b-form-select-option>
              </b-form-select>
              <div class="d-inline-block">
                <b-dropdown
                  ref="chooseDropdown"
                  class="mr-50 bg-white"
                  variant="outline-dark"
                  size="sm"
                  menu-class="filter-dropdown"
                >
                  <template v-slot:button-content>
                    {{ $tk('ClientPortal.Filters.Choose') }}
                  </template>
                  <div
                    v-for="(availableDisplayValue, availableValue) in propertyFilter.availableValues"
                    :key="availableValue"
                    class="dropdown-item"
                    for="property-check"
                  >
                    <b-form-checkbox
                      :checked="isPropertyFilterValueAdded(propertyFilter, availableValue)"
                      @change="addPropertyFilterValue(propertyFilter, availableValue)"
                    >
                      {{ availableDisplayValue }}
                    </b-form-checkbox>
                  </div>
                </b-dropdown>
              </div>
              <b-button
                :key="'check-filter-'+propertyFilterIndex"
                size="sm"
                class="pr-0"
                variant="link"
                @click="closeFilterEdit(propertyFilter)"
              >
                <GjIcon name="Checkmark" />
              </b-button>
            </template>
            <template v-else>
              <div
                class="border rounded outline-dark px-50 py-25 text-black"
              >
                <span
                  v-for="(propertyFilterValue, propertyFilterValueIndex) in propertyFilterValues[propertyFilter.property].value"
                  :key="propertyFilterValueIndex"
                  class="mr-25"
                >
                  {{ propertyFilter.availableValues[propertyFilterValue] }}
                <!-- <span v-if="propertyFilterValues[propertyFilter.property].value.length > 1 && propertyFilterValueIndex < propertyFilterValues[propertyFilter.property].value.length - 1">,</span> -->
                </span>
              </div>
              <b-button
                :key="'edit-filter-'+propertyFilterIndex"
                size="sm"
                class="pr-0"
                variant="link"
                @click="openFilterEdit(propertyFilter)"
              >
                <GjIcon
                  class="text-secondary"
                  name="Edit"
                />
              </b-button>
            </template>
          </div>
          <b-button
            :key="'remove-filter-'+propertyFilterIndex"
            size="sm"
            class="pr-0"
            variant="link"
            @click="removePropertyFilter(propertyFilter)"
          >
            <GjIcon
              class="text-secondary"
              name="Close"
            />
          </b-button>
        </div>

      </template>
      <template #footer>
        <div
          class="text-right"
        >
          <b-dropdown
            v-if="!selectedPropertyFiltersWrapper.id"
            id="dropdown-form"
            ref="saveFilterDropdown"
            class="mr-50"
            variant="outline-dark"
            size="sm"
            :text="$tk('ClientPortal.Filters.SaveFilter')"
          >
            <b-dropdown-form>
              <b-form-group
                :label="$tk('ClientPortal.Filters.FilterName')"
                label-for="dropdown-form-email"
                @submit.stop.prevent
              >
                <b-form-input
                  id="dropdown-form-email"
                  v-model="saveFilterModal.name"
                  size="sm"
                  :placeholder="$tk('ClientPortal.Filters.EnterName')"
                />
              </b-form-group>
              <div class="text-right text-nowrap">
                <b-button
                  class="mr-50"
                  variant="outline-dark"
                  size="sm"
                  :disabled="saveFilterModal.loading"
                  @click="closeSaveFilterModal"
                >
                  {{ $tk('ClientPortal.Filters.Cancel') }}
                </b-button>
                <b-button
                  variant="primary"
                  size="sm"
                  :disabled="saveFilterModal.loading"
                  @click="saveFilter"
                >
                  {{ $tk('ClientPortal.Filters.Save') }}
                </b-button>
              </div>
            </b-dropdown-form>
          </b-dropdown>
          <b-button
            variant="outline-dark"
            size="sm"
            @click="clearPropertyFilters"
          >
            {{ $tk('ClientPortal.Filters.ClearAll') }}
            <GjIcon
              class="ml-50"
              name="Close"
            />
          </b-button>
        </div>
      </template>
    </b-card>

    <!--Campaign Metrics Chart-->
    <b-skeleton
      v-if="loading"
      height="313px"
      class="mb-1"
    />
    <b-card
      v-else
      class="mb-1"
    >
      <template #header>
        <h5 class="mb-0">
          {{ $tk('ClientPortal.Property.Earnings.Graph') }}
          <loader
            v-if="loadingChartStats"
            class="d-inline-block ml-50"
            :size="15"
            :show="true"
          />
        </h5>
        <div class="d-flex flex-column flex-md-row justify-content-start align-items-center">
          <span class="mr-50">{{ $tk('ClientPortal.Property.Earnings.ShowBy') }}</span>
          <b-dropdown
            class="mr-50"
            variant="outline-dark"
            size="sm"
          >
            <template #button-content>
              <i class="chart-dot-blue mr-50" />
              {{ firstChartFilterLabelName }}
            </template>
            <b-dropdown-item
              v-for="(label, index) in wrapper.chartLabels"
              :key="'firstChartFilter-' + index"
              :value="label.value"
              :data-index="index"
              :disabled="secondChartFilterLabelName === label.name"
              :class="firstChartFilterLabelName === label.name ? 'selected-filter' : ''"
              @click="setFirstChartLabel(label.value)"
            >
              {{ label.name }}
            </b-dropdown-item>
          </b-dropdown>
          <b-dropdown
            class="mr-50"
            variant="outline-dark"
            size="sm"
          >
            <template #button-content>
              <i class="chart-dot-red mr-50" />
              {{ secondChartFilterLabelName }}
            </template>
            <b-dropdown-item
              v-for="(label, index) in wrapper.chartLabels"
              :key="'secondChartFilter-' + index"
              class="multilevel-dropdown"
              :value="label.value"
              :data-index="index"
              :disabled="firstChartFilterLabelName === label.name"
              :class="secondChartFilterLabelName === label.name ? 'selected-filter' : ''"
              @click="setSecondChartLabel(label.value)"
            >
              {{ label.name }}
            </b-dropdown-item>
          </b-dropdown>
          <span class="mr-50 ml-2">{{ $tk('ClientPortal.Property.Earnings.TimePeriod') }}</span>
          <b-dropdown
            variant="outline-dark"
            size="sm"
            right
          >
            <template #button-content>
              {{ wrapper.config.filter.chartInterval }}
            </template>
            <b-dropdown-item
              v-for="(interval, index) in wrapper.chartIntervals"
              :key="'chartIntervals-' + index"
              :value="interval.value"
              :data-index="index"
              :class="wrapper.config.filter.chartInterval === interval.value ? 'selected-filter' : ''"
              @click="setChartInterval(interval.name)"
            >
              {{ interval.name }}
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>
      <apexcharts
        ref="chart"
        height="200"
        type="line"
        :options="chartOptions"
        :series="chartStats.series"
      />
    </b-card>
    <master-table
      :wrapper="wrapper"
      :stats="stats"
      :loading="loading"
      :loading-statistics="loadingStatistics"
      :campaign-current-page="campaignCurrentPage"
      :ad-group-current-page="adGroupCurrentPage"
      :ad-current-page="adCurrentPage"
      @loadStats="loadStatistics"
      @sync="syncState"
      @tab="setTab"
      @config="saveConfig"
      @reload="reloadStatistics"
      @push="pushState"
      @chart="reloadChart"
      @changePage="changePage"
    />
  </section>
</template>

<script>
import GjIcon from '@gjirafatech/gjirafa-icons/Icon.vue'
import VueApexCharts from 'vue-apexcharts'
import DateRangePicker from '@/components/VueDateRangePicker'
import moment from 'moment'
import StatisticsService from '@/api/client/statistics.service'
import axios from 'axios'
import notifications from '@/mixins/toastification'
import loader from '@/components/Loader.vue'
import masterTable from './MasterTable.vue'

const ENTITY_TYPES = {
  CAMPAIGN: 1,
  AD_GROUP: 2,
  AD: 3,
  BREAKDOWN: 4,
  SUMMARY: 5,
}

const TABS = {
  CAMPAIGNS: 'campaigns',
  AD_GROUPS: 'adgroups',
  ADS: 'ads',
}

export default {
  components: {
    apexcharts: VueApexCharts,
    GjIcon,
    DateRangePicker,
    masterTable,
    loader,
  },
  mixins: [notifications],
  data() {
    return {
      wrapper: {},
      stats: {
        campaignTabStatistics: {
          chartStats: {},
          entities: [],
          totalEntitites: 0,
          needsReload: true,
          selectedAll: false,
        },
        adGroupTabStatistics: {
          chartStats: {},
          entities: [],
          totalEntities: 0,
          needsReload: true,
          selectedAll: false,
        },
        adTabStatistics: {
          chartStats: {},
          entities: [],
          totalEntities: 0,
          needsReload: true,
          selectedAll: false,
        },
      },

      campaignCurrentPage: 1,
      adGroupCurrentPage: 1,
      adCurrentPage: 1,

      loading: true,
      drag: false,
      loadingChartStats: true,
      savingConfig: false,

      titleWidthCampaign: 300,
      titleWidthAdgroup: 300,
      titleWidthAd: 300,
      campaignList: [],
      adGroupList: [],
      presetBck: {},
      modalOpened: false,
      hasPresetChanges: false,
      savePresetModal: {
        top: 0,
        left: 0,
        show: false,
        value: '',
      },
      saveFilterModal: {
        name: '',
        loading: false,
      },
      confirmDialogType: {
        tabType: 1,
        actionType: 1,
        loading: false,
      },
      campaignEditModel: null,
      adGroupEditModel: null,
      adEditModel: null,

      selectedPropertyFilters: [],
      selectedPropertyFiltersWrapper: {},

      heatmapModel: {
        adName: '',
        width: 0,
        height: 0,
        mediaUrl: '',
        adSubtype: '',
        coordinates: [],
      },
      heatmapInstance: {

      },
      predefinedRanges: {
        today: {
          label: this.$tk('ClientPortal.PredefinedRanges.Today'),
          startDate: moment(),
          endDate: moment(),
        },
        yesterday: {
          label: this.$tk('ClientPortal.PredefinedRanges.Yesterday'),
          startDate: moment().subtract(1, 'days'),
          endDate: moment().subtract(1, 'days'),
        },
        last7Days: {
          label: this.$tk('ClientPortal.PredefinedRanges.Last7Days'),
          startDate: moment().subtract(7, 'days'),
          endDate: moment().subtract(1, 'days'),
        },
        last30Days: {
          label: this.$tk('ClientPortal.PredefinedRanges.Last30Days'),
          startDate: moment().subtract(30, 'days'),
          endDate: moment().subtract(1, 'days'),
        },
        lastMonth: {
          label: this.$tk('ClientPortal.PredefinedRanges.LastMonth'),
          startDate: moment().subtract(1, 'months').startOf('month'),
          endDate: moment().subtract(1, 'month').endOf('month'),
        },
        defaultRange: {
          label: this.$tk('ClientPortal.PredefinedRanges.DefaultRange'),
          startDate: moment().subtract(7, 'days'),
          endDate: moment(),
        },
      },
      defaultRange: '',
      name: 'BootstrapVue',
      isDropdownMultilevelVisible: false,
      value: [],
      loadingStatistics: false,
      menuItems: [
        {
          name: 'Item 1',
          children: [{ name: 'Subitem 1' }, { name: 'Subitem 2' }, { name: 'Subitem 3' }],
        },
        {
          name: 'Item 2',
        },
      ],
    }
  },
  computed: {
    propertyFilterValues() {
      return this.wrapper.config.filter.propertyFilters
    },
    chartStats() {
      switch (this.wrapper.tab) {
        case TABS.CAMPAIGNS: return this.stats.campaignTabStatistics.chartStats
        case TABS.AD_GROUPS: return this.stats.adGroupTabStatistics.chartStats
        case TABS.ADS: return this.stats.adTabStatistics.chartStats
        default: return this.stats.campaignTabStatistics.chartStats
      }
    },
    chartOptions() {
      const $this = this
      return {
        chart: {
          shadow: {
            enabled: true,
            color: '#000',
            top: 18,
            blur: 10,
            opacity: 1,
          },
          toolbar: {
            show: false,
          },
        },
        colors: ['#1D79F2', '#E34850'],
        dataLabels: {
          enabled: true,
          formatter(val) {
            return val.toLocaleString()
          },
        },
        stroke: {
          curve: 'smooth',
        },
        grid: {
          borderColor: '#e7e7e7',
          row: {
            colors: ['#f3f3f3', 'transparent'], // takes an array which will be repeated on columns
            opacity: 0.5,
          },
          padding: {
            left: 30,
            right: 0,
          },
        },
        markers: {
          size: 5,
        },
        legend: {
          show: false,
        },
        xaxis: {
          categories: $this.chartStats.xAxisLabels,
        },
        yaxis: {
          decimalsInFloat: 2,
          labels: {
            formatter(value) {
              return value.toLocaleString()
            },
            align: 'left',
            offsetX: -10,
          },
        },
        tooltip: {
          x: {
            formatter(index) {
              return $this.chartStats.xAxisLabels[index - 1]
            },
          },
          y: {
            title: {
              formatter(seriesName) {
                return seriesName
              },
            },
            formatter(value, { seriesIndex, dataPointIndex }) {
              return $this.chartStats.series[seriesIndex].tooltips[dataPointIndex].value
            },
          },
          z: {
            title: 'Title',
          },
        },
      }
    },
    firstChartFilterLabelName() {
      const index = this.wrapper.chartLabels.findIndex(x => x.value === this.wrapper.config.filter.firstChartLabel)
      return this.wrapper.chartLabels[index].name
    },
    secondChartFilterLabelName() {
      const index = this.wrapper.chartLabels.findIndex(x => x.value === this.wrapper.config.filter.secondChartLabel)
      return this.wrapper.chartLabels[index].name
    },
  },
  watch: {
    '$route.params.accountId': 'updateAccountStatistics',
  },
  async created() {
    this.loading = true
    const { cfg, tab } = this.$route.query
    try {
      const response = await StatisticsService.getStatistics(cfg, tab || TABS.CAMPAIGNS, this.$route.params.accountId)
      this.$set(this, 'wrapper', response)
    } catch (error) {
      this.error(error.message)
    } finally {
      this.syncState()
      this.loadStatistics()
      this.syncPropertyFilters()
      this.defaultRange = this.wrapper.config.filter.dateRange
      this.loading = false
      this.loadingChartStats = false
    }
  },
  mounted() {
    // eslint-disable-next-line func-names
    window.onpopstate = event => {
      const documentLocation = document.location
      const url = new URL(documentLocation)
      let tab = url.searchParams.get('tab')
      if (tab == null) tab = TABS.CAMPAIGNS
      if (event.state != null) {
        this.stats = event.state.stats
        this.wrapper.config = event.state.config
      }
      this.syncPropertyFilters()
      this.setTab(tab)
      this.loading = false
    }
    this.$root.$on('bv::dropdown::show', bvEvent => {
      if (bvEvent.componentId === 'dropdown-multilevel') {
        this.isDropdownMultilevelVisible = true
      }
    })
    this.$root.$on('bv::dropdown::hide', bvEvent => {
      if (bvEvent.componentId === 'dropdown-multilevel') {
        this.isDropdownMultilevelVisible = false
      }
      if (this.isDropdownMultilevelVisible) {
        bvEvent.preventDefault()
      }
    })
  },
  methods: {
    async updateAccountStatistics() {
      const { accountId } = this.$route.params
      if (!accountId) { return }
      this.loading = true
      this.stats.campaignTabStatistics.needsReload = true
      this.stats.adGroupTabStatistics.needsReload = true
      this.stats.adTabStatistics.needsReload = true
      this.stats.campaignTabStatistics.entities = []
      this.stats.adGroupTabStatistics.entities = []
      this.stats.adTabStatistics.entities = []
      this.selectedPropertyFiltersWrapper = {}
      const { cfg, tab } = this.$route.query
      try {
        const response = await StatisticsService.getStatistics(cfg, tab || TABS.CAMPAIGNS, accountId)
        this.$set(this, 'wrapper', response)
      } catch (error) {
        this.error(error.message)
      } finally {
        this.syncState()
        this.loadStatistics()
        this.syncPropertyFilters()
        this.defaultRange = this.wrapper.config.filter.dateRange
        this.$refs.dateRangePicker.selectRange('defaultRange')
      }
      this.$root.$on('bv::dropdown::show', bvEvent => {
        if (bvEvent.componentId === 'dropdown-multilevel') {
          this.isDropdownMultilevelVisible = true
        }
      })
      this.$root.$on('bv::dropdown::hide', bvEvent => {
        if (bvEvent.componentId === 'dropdown-multilevel') {
          this.isDropdownMultilevelVisible = false
        }
        if (this.isDropdownMultilevelVisible) {
          bvEvent.preventDefault()
        }
      })
    },
    // date-time-range methods
    submitDateRange(range) {
      const startDate = range.startDate.format('DD/MM/YYYY')
      const endDate = range.endDate.format('DD/MM/YYYY')
      const dateRange = `${startDate} - ${endDate}`
      if (this.wrapper.config.filter.dateRange === dateRange) {
        this.closeDateRangePicker()
        return
      }
      this.defaultRange = dateRange
      this.wrapper.config.filter.compare = range.compare
      this.wrapper.config.filter.dateRange = dateRange
      this.closeDateRangePicker()

      this.reloadStatistics()
      this.saveConfig(true)
    },
    closeDateRangePicker() {
      this.$refs.dateRangePickerDropdown.hide(true)
    },
    // toogleItem(index) {
    //   if (this.menu[index].children) {
    //     if (!this.menu[index].showSubMenu) {
    //       this.menu[index].showSubMenu = true
    //     } else {
    //       this.menu[index].showSubMenu = false
    //     }
    //   }
    // },
    // Pagination
    changePage(value) {
      switch (this.wrapper.tab) {
        case TABS.CAMPAIGNS: this.campaignCurrentPage = value
          break
        case TABS.AD_GROUPS: this.adGroupCurrentPage = value
          break
        case TABS.ADS: this.adCurrentPage = value
          break
        default: this.campaignCurrentPage = value
          break
      }
      this.loadStatistics()
    },
    // Statistics methods
    loadStatistics() {
      switch (this.wrapper.tab) {
        case TABS.CAMPAIGNS: this.fetchStatistics(this.stats.campaignTabStatistics, 'campaignPage', this.campaignCurrentPage, ENTITY_TYPES.CAMPAIGN, this.wrapper.config.filter.campaignIds)
          break
        case TABS.AD_GROUPS: this.fetchStatistics(this.stats.adGroupTabStatistics, 'adGroupPage', this.adGroupCurrentPage, ENTITY_TYPES.AD_GROUP, this.wrapper.config.filter.adGroupIds)
          break
        case TABS.ADS: this.fetchStatistics(this.stats.adTabStatistics, 'adPage', this.adCurrentPage, ENTITY_TYPES.AD, this.wrapper.config.filter.adIds)
          break
        default: this.fetchStatistics(this.stats.campaignTabStatistics, 'campaignPage', this.campaignCurrentPage, ENTITY_TYPES.CAMPAIGN, this.wrapper.config.filter.campaignIds)
          break
      }
    },
    async fetchStatistics(tabWrapper, pageProperty, step, entityType, selectedIds) {
      if ((this.wrapper.config.filter[pageProperty] === step - 1) && !tabWrapper.needsReload) {
        return
      }

      this.loadingStatistics = true
      this.loadingChartStats = true
      this.wrapper.config.filter[pageProperty] = step - 1
      // tabWrapper.entities = []
      this.saveConfig()

      axios.all([
        await StatisticsService.filter(this.wrapper.config.filter, entityType, this.$route.params.accountId),
        await StatisticsService.getChart(this.wrapper.config.filter, entityType, this.$route.params.accountId),
      ]).then(axios.spread((tableData, chartData) => {
        tableData.entities.forEach(entity => {
          entity.selected = selectedIds.indexOf(entity.id) >= 0
        })
        tabWrapper.entities = tableData.entities
        tabWrapper.totalEntities = tableData.total
        tabWrapper.selectedAll = this.areAllSelected(tabWrapper)
        tabWrapper.chartStats = chartData
        tabWrapper.needsReload = false
        this.syncState()
      })).finally(() => {
        this.loadingStatistics = false
        this.loading = false
        this.loadingChartStats = false
      })
    },
    async reloadChart() {
      this.loadingChartStats = true
      let tabWrapper = {}
      let entityType = ENTITY_TYPES.CAMPAIGN
      switch (this.wrapper.tab) {
        // eslint-disable-next-line no-lone-blocks
        case TABS.CAMPAIGNS: {
          tabWrapper = this.stats.campaignTabStatistics
          entityType = ENTITY_TYPES.CAMPAIGN
        } break
        // eslint-disable-next-line no-lone-blocks
        case TABS.AD_GROUPS: {
          tabWrapper = this.stats.adGroupTabStatistics
          entityType = ENTITY_TYPES.AD_GROUP
        } break
        // eslint-disable-next-line no-lone-blocks
        case TABS.ADS: {
          tabWrapper = this.stats.adTabStatistics
          entityType = ENTITY_TYPES.AD
        } break
        default:
          return false
      }

      try {
        const chartData = await StatisticsService.getChart(this.wrapper.config.filter, entityType, this.$route.params.accountId)
        this.$set(tabWrapper, 'chartStats', chartData)
        this.syncState()
      } catch (error) {
        this.error(error.message)
      } finally {
        this.loadingChartStats = false
      }
      return true
    },
    syncState() {
      var state = {
        stats: this.stats,
        config: this.wrapper.config,
      }
      window.history.replaceState(state, '', window.location)
    },
    setTab(tab) {
      this.$set(this.wrapper, 'tab', tab)
      this.loadStatistics()
    },
    areAllSelected(tabWrapper) {
      if (tabWrapper.entities.length === 0) return false

      let selectedEntities = 0
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < tabWrapper.entities.length; i++) {
        const entity = tabWrapper.entities[i]
        // eslint-disable-next-line no-plusplus
        if (entity.selected === true) selectedEntities++
      }
      return selectedEntities === tabWrapper.entities.length
    },
    syncPropertyFilters() {
      this.selectedPropertyFilters.splice(0, this.selectedPropertyFilters.length)
      this.wrapper.propertyFilters.forEach(propertyFilter => {
        if (propertyFilter.type === 1) {
          propertyFilter.children.forEach(childPropertyFilter => {
            if (this.isPropertyFilterAdded(childPropertyFilter)) {
              this.selectedPropertyFilters.push(childPropertyFilter)
            }
          })
        } else if (this.isPropertyFilterAdded(propertyFilter)) {
          this.selectedPropertyFilters.push(propertyFilter)
        }
      })
      this.wrapper.searchFilters.forEach(propertyFilter => {
        if (propertyFilter.type === 1) {
          propertyFilter.children.forEach(childPropertyFilter => {
            if (this.isPropertyFilterAdded(childPropertyFilter)) {
              this.selectedPropertyFilters.push(childPropertyFilter)
            }
          })
        } else if (this.isPropertyFilterAdded(propertyFilter)) {
          this.selectedPropertyFilters.push(propertyFilter)
        }
      })
    },
    isPropertyFilterAdded(propertyFilter) {
      return this.propertyFilterValues[propertyFilter.property]
    },
    addPropertyFilter(propertyFilter) {
      if (propertyFilter.type === 1) return
      const { selectedPropertyFilters } = this
      const filterIndex = selectedPropertyFilters.map(e => e.property).indexOf(propertyFilter.property)
      if (filterIndex < 0) {
        selectedPropertyFilters.push(propertyFilter)
      }
      const propertyFilterValues = this.wrapper.config.filter.propertyFilters
      if (!propertyFilterValues[propertyFilter.property]) {
        this.$set(propertyFilterValues, propertyFilter.property, JSON.parse(JSON.stringify(propertyFilter.defaultValue)))
        if (propertyFilter.type === 2 || propertyFilter.type === 3) {
          this.openFilterEdit(propertyFilter)
        }
      }
      if (propertyFilter.type !== 4 && propertyFilter.type !== 3) {
        this.$nextTick(() => {
          if (this.$refs.propertyFilterInput.length > 0) {
            this.$refs.propertyFilterInput[0].focus()
          }
        })
      }
      this.wrapper.config.filter.adIds = []
      this.wrapper.config.filter.adGroupIds = []
      this.wrapper.config.filter.campaignIds = []
      this.$set(this, 'selectedPropertyFiltersWrapper', {})
      // this.$refs.filterDropdown.hide(true)
    },
    addPropertyFilterValue(propertyFilter, propertyFilterValue) {
      this.addPropertyFilter(propertyFilter)
      const propertyFilterValues = this.wrapper.config.filter.propertyFilters
      const { property } = propertyFilter
      const index = propertyFilterValues[property].value.indexOf(propertyFilterValue)
      if (index < 0) {
        propertyFilterValues[property].value.push(propertyFilterValue)
      } else {
        propertyFilterValues[property].value.splice(index, 1)
        if (propertyFilterValues[property].value.length === 0) {
          this.removePropertyFilter(propertyFilter)
        }
      }

      this.onFiltersChanged()
      this.$set(this, 'selectedPropertyFiltersWrapper', {})
    },
    addPropertyFilterTagValue(event, propertyFilter) {
      if (event.which === 9) {
        event.preventDefault()
      }

      propertyFilter.value.push(event.target.value)
      const formTags = this.$refs.propertyFilterFormTags[0]
      formTags.newTag = ''
      event.target.autofocus = true
    },
    isPropertyFilterValueAdded(propertyFilter, propertyFilterValue) {
      return this.isPropertyFilterAdded(propertyFilter) && this.propertyFilterValues[propertyFilter.property].value.indexOf(propertyFilterValue) >= 0
    },
    removePropertyFilter(propertyFilter) {
      const { selectedPropertyFilters } = this
      const filterIndex = selectedPropertyFilters.map(e => e.property).indexOf(propertyFilter.property)
      if (filterIndex >= 0) {
        selectedPropertyFilters.splice(filterIndex, 1)
      }

      const propertyFilterValues = this.wrapper.config.filter.propertyFilters
      if (propertyFilterValues[propertyFilter.property]) {
        this.$delete(propertyFilterValues, propertyFilter.property)
      }

      this.onFiltersChanged()
      this.$set(this, 'selectedPropertyFiltersWrapper', {})
    },
    openFilterEdit(filter) {
      this.$set(filter, 'inEditMode', true)
    },
    closeFilterEdit(filter) {
      if (filter.type === 3 && this.wrapper.config.filter.propertyFilters[filter.property].value.length === 0) return

      this.$set(filter, 'inEditMode', false)
      this.onFiltersChanged(true)
    },
    onFiltersChanged() {
      this.saveConfig(true)
      this.reloadStatistics()
    },
    setSavedPropertyFilters(propertyFilterWrapper) {
      const savedFilter = JSON.parse(JSON.stringify(propertyFilterWrapper))
      this.$set(this, 'selectedPropertyFiltersWrapper', savedFilter)
      this.$set(this.wrapper.config.filter, 'propertyFilters', savedFilter.values)
      this.syncPropertyFilters()
      this.onFiltersChanged()
      this.$refs.filterDropdown.hide(true)
    },
    async saveConfig(pushState = false) {
      if (this.savingConfig || this.isEditingPropertyFilters) {
        return
      }
      this.savingConfig = true
      try {
        const response = await StatisticsService.createConfig(this.wrapper.config, this.$route.params.accountId)
        if (response.saved) {
          if (pushState) {
            const { configId } = response
            const documentLocation = document.location
            const url = new URL(documentLocation)
            url.searchParams.set('cfg', configId)
            this.pushState(url.toString())
          }
        } else {
          this.error(response.message)
        }
      } catch (error) {
        this.error(error.message)
      } finally {
        this.savingConfig = false
      }
    },
    reloadStatistics() {
      this.stats.campaignTabStatistics.needsReload = true
      this.stats.adGroupTabStatistics.needsReload = true
      this.stats.adTabStatistics.needsReload = true
      this.loadStatistics()
    },
    pushState(url) {
      const state = {
        stats: this.stats,
        config: this.wrapper.config,
      }
      window.history.pushState(state, '', url)
    },
    clearPropertyFilters() {
      this.selectedPropertyFilters.splice(0, this.selectedPropertyFilters.length)
      this.$set(this, 'selectedPropertyFiltersWrapper', {})
      Object.keys(this.wrapper.config.filter.propertyFilters).forEach(key => {
        this.$delete(this.wrapper.config.filter.propertyFilters, key)
      })
      this.reloadStatistics()
    },
    // save filter modal
    async saveFilter() {
      if (this.saveFilterModal.loading) {
        return
      }

      this.saveFilterModal.loading = true
      const propertyFilterValues = this.wrapper.config.filter.propertyFilters
      const { name } = this.saveFilterModal
      const propertyFiltersWrapper = { name, values: propertyFilterValues }
      try {
        const response = await StatisticsService.createPropertyFilters(propertyFiltersWrapper, this.$route.params.accountId)
        if (response.inserted) {
          this.wrapper.savedPropertyFilters.push(response.model)
          this.$set(this, 'selectedPropertyFiltersWrapper', response.model)
          this.closeSaveFilterModal()
          this.saveFilterModal.loading = false
          this.saveFilterModal.name = ''
          this.success(this.$tk('ClientPortal.Statistics.SavedSuccesfully'))
        } else {
          this.error(this.$tk('ClientPortal.Statistics.SomethingWentWrong'))
        }
      } catch (error) {
        this.error(error.message)
      } finally {
        this.saveFilterModal.loading = false
      }
      this.$refs.saveFilterDropdown.hide(true)
    },
    closeSaveFilterModal() {
      this.$refs.saveFilterDropdown.hide(true)
    },
    // Chart methods
    setFirstChartLabel(label) {
      this.wrapper.config.filter.firstChartLabel = label
      this.saveConfig(true)
      this.reloadChart()
    },
    setSecondChartLabel(label) {
      this.wrapper.config.filter.secondChartLabel = label
      this.saveConfig(true)
      this.reloadChart()
    },
    setChartInterval(interval) {
      if (this.wrapper.config.filter.chartInterval === interval) return

      this.wrapper.config.filter.chartInterval = interval
      this.saveConfig(true)
      this.reloadChart()
    },
  },
}
</script>

<style lang="scss">
@import '@/assets/scss/views/client/ads-manager/statistics/statistics.scss';
</style>
